import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, Printer, FileDoc, Eye, Cookie } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';
import { DZ_BAR_LOGO_DATA_URL } from '../assets/dzBarLogo';

export default function BriseviPage() {
  const [zahtjevi, setZahtjevi] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    vrsta_zahtjeva: 'brisevi',
    objekat_naziv_adresa: '', broj_uzoraka: 0, vrsta_analize: 'mikrobioloska',
    client_id: '', podnosilac_naziv: '', podnosilac_adresa: '', podnosilac_pib: '',
    podnosilac_pdv: '', podnosilac_kontakt: '', nacin_preuzimanja: 'licno',
    mjesto_podnosenja: '', datum_podnosenja: new Date().toISOString().split('T')[0],
    kontakt_osoba: '', kontakt_telefon: '',
    metode_uzorkovanja_prihvata: true, metode_uzorkovanja_posebni: '',
    metode_ispitivanja_prihvata: true, metode_ispitivanja_posebni: '',
    izjava_usaglasenosti: false, izjava_specifikacija: '',
    pravilo_odlucivanja: 'pravilo_1', saglasnost_eksterna: true, ovlasceno_lice: '', napomena: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [z, c] = await Promise.all([api.get('/zahtjevi?vrsta=brisevi'), api.get('/clients')]);
      setZahtjevi(z.data); setClients(c.data);
    } catch { toast.error('Greška pri učitavanju'); }
    finally { setLoading(false); }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) setForm(prev => ({
      ...prev, client_id: clientId, podnosilac_naziv: c.naziv_firme || c.skraceni_naziv,
      podnosilac_adresa: [c.adresa, c.grad].filter(Boolean).join(', '),
      podnosilac_pib: c.pib || '', podnosilac_pdv: c.pdv_status === 'obveznik' ? `ME${c.pib}` : '',
      podnosilac_kontakt: c.telefon || '', kontakt_osoba: c.direktor || '', kontakt_telefon: c.telefon || '',
      ovlasceno_lice: c.direktor || '',
    }));
  };

  // Auto-fill when editing with existing client
  useEffect(() => {
    if (isFormOpen && form.client_id && clients.length > 0 && !form.podnosilac_naziv) {
      handleClientSelect(form.client_id);
    }
  }, [isFormOpen, clients]);

  const handleSubmit = async () => {
    if (!form.objekat_naziv_adresa || !form.podnosilac_naziv) { toast.error('Unesite podatke o objektu i podnosiocu'); return; }
    try {
      if (editingId) { await api.put(`/zahtjevi/${editingId}`, form); toast.success('Zahtjev ažuriran'); }
      else { await api.post('/zahtjevi', form); toast.success('Zahtjev kreiran'); }
      setIsFormOpen(false); setEditingId(null); setForm(emptyForm); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const handleEdit = (z) => {
    setEditingId(z.id);
    setForm({ ...emptyForm, ...z, vrsta_zahtjeva: 'brisevi' });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi/${id}`); toast.success('Obrisano'); fetchData(); } catch { toast.error('Greška'); }
  };

  const buildHtml = (z) => {
    const cb = (v) => v ? '&#9746;' : '&#9744;';
    const logoUrl = DZ_BAR_LOGO_DATA_URL;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>BRIS Zahtjev</title>
<style>
@page{size:A4;margin:10mm 15mm 10mm 15mm}
@media print{html,body{-webkit-print-color-adjust:exact}}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Times New Roman',serif;font-size:12pt;color:#000;line-height:1.4;background:#fff}
table{border-collapse:collapse}
.htbl{width:100%;border:1.5px solid #000;margin-bottom:6px}
.htbl td{border:1px solid #000;padding:4px 6px;vertical-align:middle}
.htbl .logo-cell{width:90px;text-align:center;padding:4px}
.htbl .logo-cell img{width:78px;height:auto}
.htbl .info-cell{text-align:center;font-size:11pt;line-height:1.35}
.htbl .info-cell .title{font-weight:bold;font-size:13pt;margin-top:3px}
.htbl .meta-cell{width:95px;font-size:10pt;text-align:center}
.ftbl{width:100%;border-collapse:collapse;margin-top:2px}
.ftbl td{border:1px solid #000;padding:5px 7px;font-size:11.5pt;vertical-align:top;min-height:22px}
.ftbl .lbl{font-weight:bold;white-space:nowrap}
.sect{font-weight:bold;font-size:12pt;margin:8px 0 2px 0;text-decoration:underline}
.note{font-size:10pt;margin:5px 0;line-height:1.35;text-align:justify}
.note-bold{font-weight:bold;font-size:10.5pt;margin:5px 0;line-height:1.35}
.cb-row{margin:3px 0 3px 12px;font-size:11.5pt}
.tbl{width:100%;border-collapse:collapse;margin:4px 0;font-size:11pt}
.tbl td{border:1px solid #000;padding:4px 6px;vertical-align:top}
.sign-row{display:flex;align-items:baseline;margin-top:15px;font-size:12pt;gap:10px}
.field-line{border-bottom:1px solid #000;min-height:16px;padding:0 3px;display:inline-block;min-width:180px}
.field-line-full{border-bottom:1px solid #000;display:block;min-height:20px;padding:2px 5px;margin:2px 0;font-size:11.5pt}
.page-break{page-break-before:always}
.version{font-size:9pt;margin-top:8px}
.meta-print{margin-top:5px;font-size:8pt;color:#555}
@media print{.meta-print{display:none}}
</style></head><body>

<!-- STRANA 1 -->
<table class="htbl">
<tr>
  <td class="logo-cell" rowspan="2"><img src="${logoUrl}" alt="DZ Bar Logo"></td>
  <td class="info-cell" rowspan="2">
    <strong>ZU &nbsp;Dom zdravlja - Bar</strong><br>
    <strong>ODSJEK ZA HIGIJENU I ZDRAVSTVENU EKOLOGIJU</strong><br>
    Tel: 030/314-885 &nbsp;&nbsp; Tel/fax: 030/311-001<br>
    e-mail: higijenadzbar@gmail.com<br>
    Br.žiro računa: 530-2470-72<br>
    <div class="title">ZAHTJEV ZA UZORKOVANJE I ISPITIVANJE<br>HIGIJENE POVRŠINA METODOM BRISA</div>
  </td>
  <td class="meta-cell" style="border-bottom:1px solid #000"><strong>Oznaka</strong><br>Q4.7.1.00.02</td>
</tr>
<tr><td class="meta-cell"><strong>Str./ukupno<br>str.</strong><br>1/2</td></tr>
</table>

<div style="font-size:11.5pt;margin:6px 0 4px 0;text-align:justify">Molim Vas da obezbijedite ekipu radi uzimanja uzoraka briseva sa površina, opreme, predmeta i ruku zaposlenih koji rade sa hranom, u objektu:</div>

<table class="ftbl">
<tr><td colspan="4"><span class="lbl">Podaci o objektu (navesti tačan naziv i adresu):</span><br>${z.objekat_naziv_adresa||''}</td></tr>
<tr><td colspan="2"><span class="lbl">Broj uzoraka:</span> ${z.broj_uzoraka||''}</td><td colspan="2"><span class="lbl">a) Za mikrobiološku analizu</span></td></tr>
</table>

<div style="font-weight:bold;font-size:12pt;margin:6px 0 2px 0">Podaci o podnosiocu zahtjeva:</div>
<table class="ftbl">
<tr><td colspan="4"><span class="lbl">Naziv (institucija, firma, pravno/fizičko lice i sl.):</span><br>${z.podnosilac_naziv||''}</td></tr>
<tr><td colspan="4"><span class="lbl">Adresa:</span> ${z.podnosilac_adresa||''}</td></tr>
<tr><td colspan="2"><span class="lbl">PIB:</span> ${z.podnosilac_pib||''}</td><td colspan="2"><span class="lbl">PDV:</span> ${z.podnosilac_pdv||''}</td></tr>
<tr><td colspan="4"><span class="lbl">Kontakt tel/FAX:</span> ${z.podnosilac_kontakt||''}</td></tr>
</table>

<div class="note-bold" style="margin-top:8px">NAPOMENA: IZVJEŠTAJI SE PREUZIMAJU NAKON 7 DANA OD DANA UZORKOVANJA, UZ DOKAZ O IZVRŠENOJ UPLATI. Cijena uzorkovanja i analize zavise od broja uzetih briseva.</div>

<div style="font-size:11.5pt;margin:6px 0 3px 0;font-weight:bold">Označiti način preuzimanja izvještaja:</div>
<div style="margin:2px 0 2px 10px;font-size:11.5pt">${cb(z.nacin_preuzimanja==='licno')} 1) lično u Odsjeku za higijenu i zdravstvenu ekologiju</div>
<div style="margin:2px 0 2px 10px;font-size:11.5pt">${cb(z.nacin_preuzimanja==='postom')} 2) poštom na adresu navedenu u zahtjevu</div>

<div class="note" style="margin-top:6px">Sve informacije dobijene ili nastale u toku realizacije ugovorenih aktivnosti predstavljaju vlasništvo korisnika i smatraju se povjerljivim. Ukoliko se zakonom zahtjeva da se neke povjerljive informacije otkriju, Odsjek/Laboratorija će učiniti te informacije dostupnim i o tome informisati korisnika, izuzev ako je to zakonom zabranjeno.</div>

<div style="font-size:12pt;font-weight:bold;text-align:center;margin:8px 0">ZAHTJEV ISPUNITI ČITKO!</div>

<table class="ftbl">
<tr><td colspan="2"><span class="lbl">Mjesto podnošenja zahtjeva:</span> ${z.mjesto_podnosenja||''}</td><td colspan="2"><span class="lbl">Kontakt osoba:</span> ${z.kontakt_osoba||''}</td></tr>
<tr><td colspan="2"><span class="lbl">Datum:</span> ${formatDatePrint(z.datum_podnosenja)||''}</td><td colspan="2"><span class="lbl">Broj telefona:</span> ${z.kontakt_telefon||''}</td></tr>
</table>

<div style="text-align:right;margin-top:20px;font-size:11.5pt">
  <span style="margin-right:80px">m.p.</span>
</div>
<div class="version">Verzija/izmjena: 04/00 &nbsp;&nbsp; datum: 18.09.2025.</div>

<!-- STRANA 2 -->
<div class="page-break"></div>
<table class="htbl">
<tr>
  <td class="logo-cell" rowspan="2"><img src="${logoUrl}" alt="DZ Bar Logo"></td>
  <td class="info-cell" rowspan="2">
    <strong>ZU &nbsp;Dom zdravlja - Bar</strong><br>
    <strong>ODSJEK ZA HIGIJENU I ZDRAVSTVENU EKOLOGIJU</strong><br>
    Tel: 030/314-885 &nbsp;&nbsp; Tel/fax: 030/311-001<br>
    e-mail: higijenadzbar@gmail.com<br>
    Br.žiro računa: 530-2470-72<br>
    <div class="title">ZAHTJEV ZA UZORKOVANJE I ISPITIVANJE<br>HIGIJENE POVRŠINA METODOM BRISA</div>
  </td>
  <td class="meta-cell" style="border-bottom:1px solid #000"><strong>Oznaka</strong><br>Q4.7.1.00.02</td>
</tr>
<tr><td class="meta-cell"><strong>Str./ukupno<br>str.</strong><br>2/2</td></tr>
</table>

<div class="sect">Zahtijevane metode uzorkovanja</div>
<table class="tbl">
<tr><td style="width:65%">Prihvata se metode uzorkovanja iz Obima akreditacije Odsjeka za higijenu i zdravstvenu ekologiju</td><td style="text-align:center">${cb(z.metode_uzorkovanja_prihvata)} DA &nbsp; ${cb(!z.metode_uzorkovanja_prihvata)} NE</td></tr>
<tr><td>Posebni zahtjevi za metode uzorkovanja:</td><td style="text-align:center">${cb(!!z.metode_uzorkovanja_posebni)} DA &nbsp; ${cb(!z.metode_uzorkovanja_posebni)} NE<br><span style="font-size:10.5pt">Navesti koji: ${z.metode_uzorkovanja_posebni||'_________'}</span></td></tr>
</table>

<div class="sect">Zahtijevane metode ispitivanja</div>
<table class="tbl">
<tr><td style="width:65%">Prihvataju se metode iz Obima akreditacije Laboratorije sanitarne mikrobiologije</td><td style="text-align:center">${cb(z.metode_ispitivanja_prihvata)} DA &nbsp; ${cb(!z.metode_ispitivanja_prihvata)} NE</td></tr>
<tr><td>Posebni zahtjevi za metode ispitivanja:</td><td style="text-align:center">${cb(!!z.metode_ispitivanja_posebni)} DA &nbsp; ${cb(!z.metode_ispitivanja_posebni)} NE<br><span style="font-size:10.5pt">Navesti koji: ${z.metode_ispitivanja_posebni||'_________'}</span></td></tr>
</table>

<div class="sect">Zahtjev za izjavu o usaglašenosti</div>
<div style="margin:3px 0;font-size:11.5pt">Potrebna izjava o usaglašenosti: &nbsp; ${cb(z.izjava_usaglasenosti)} DA &nbsp; ${cb(!z.izjava_usaglasenosti)} NE</div>
<div style="margin:3px 0;font-size:11.5pt">Ukoliko je potrebna, navesti prema kojoj specifikaciji (pravilnik, zakon, standard i sl.): <span style="border-bottom:1px solid #000;display:inline-block;min-width:200px;padding:0 3px">${z.izjava_specifikacija||'&nbsp;'}</span></div>

<div class="sect">Izjava korisnika o Pravilu odlučivanja za ocjenu usaglašenosti uzorka:</div>
<div class="note" style="font-size:11pt;margin-bottom:3px">Napomena: Objašnjenja data u Uputstvu Q3.7.8.00.02 dostupno na zahtjev korisnika usluga</div>
<div class="note">Obaviješteni smo i saglasni sa Pravilom odlučivanja i načinom izvještavanja o rezultatima ispitivanja Laboratorija Doma zdravlja Bar i zahtijevamo da se primjenjuje:</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='specifikacija')} Ocjena usaglašenosti u skladu sa pravilom odlučivanja definisanim u specifikaciji;</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='pravilo_1')} Pravilo 1: Pravilo podijeljenog rizika</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='pravilo_2')} Pravilo 2: Pravilo sigurnog odbijanja rezultata</div>
<div class="cb-row">${cb(z.pravilo_odlucivanja==='pravilo_3')} Pravilo 3: Pravilo sigurnog prihvatanja rezultata</div>
<div class="note" style="margin-top:4px">U svim slučajevima kada klijent prepusti Laboratoriji Doma zdravlja Bar odluku o tome koje se pravilo odlučivanja primjenjuje, primijeniće se Pravilo 1: Pravilo podijeljenog rizika.</div>

<div class="sign-row">
  <span style="margin-right:30px">M.P.</span>
  <span>Ovlašćeno lice korisnika usluga: </span><span style="flex:1;border-bottom:1px solid #000;min-height:14px;padding:0 3px">${z.ovlasceno_lice||'&nbsp;'}</span>
</div>

<div class="sect" style="margin-top:12px">IZJAVA KORISNIKA O SAGLASNOSTI ZA EKSTERNO NABAVLJENU USLUGU UZORKOVANJA I ISPITIVANJA</div>
<div class="note">(Popuniti kad je primjenljivo):</div>
<div class="note" style="margin-top:4px">Upoznati smo i saglasni da se uzorkovanja/ispitivanja koja se ne mogu izvršiti u Laboratorijama Doma zdravlja Bar, izvrše u nekoj od eksterno angažovanih kompetentnih Laboratorija, po izboru Doma zdravlja Bar i uz saglasnost korisnika a čiji su rezultati sastavni dio Izvještaja o ispitivanju Laboratorija Doma zdravlja Bar.</div>

<div class="sign-row" style="margin-top:20px">
  <span>Datum: </span><span style="border-bottom:1px solid #000;display:inline-block;width:110px;padding:0 3px">${formatDatePrint(z.datum_podnosenja)||'&nbsp;'}</span>
  <span style="margin:0 15px">M.P.</span>
  <span>Ovlašćeno lice korisnika usluga: </span><span style="flex:1;border-bottom:1px solid #000;min-height:14px;padding:0 3px">${z.ovlasceno_lice||'&nbsp;'}</span>
</div>
<div class="version">Verzija/izmjena: 04/00 &nbsp;&nbsp; datum: 18.09.2025.</div>
<div class="meta-print">Kreirao: ${z.created_by_name||'N/A'} | FINANCE GROUP CRM</div>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script></body></html>`;
  };

  const handlePrint = (z) => printHTML(buildHtml(z));
  const handleWord = (z) => downloadWord(buildHtml(z), `BRIS_${(z.podnosilac_naziv||'zahtjev').replace(/\s+/g,'_')}.doc`);

  return (
    <div className="space-y-6" data-testid="brisevi-page">
      <div className="flex justify-between items-center">
        <div><h3 className="text-lg font-medium text-slate-900">Zahtjev za briseve (BRIS)</h3><p className="text-sm text-slate-600 mt-1">Uzorkovanje i ispitivanje higijene površina</p></div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setIsFormOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="brisevi-add-btn"><Plus size={20} weight="bold" className="mr-2" /> Novi zahtjev</Button>
      </div>
      {loading ? <div className="flex items-center justify-center h-64"><p className="text-slate-500">Učitavanje...</p></div> : zahtjevi.length === 0 ? (
        <Card className="p-8 text-center"><Cookie size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" /><p className="text-slate-500">Nema zahtjeva za briseve</p></Card>
      ) : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm text-slate-600" data-testid="brisevi-table"><thead className="bg-slate-50 border-b"><tr>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Objekat</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Podnosilac</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Br. uzoraka</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Formirao</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Status</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
        </tr></thead><tbody>
          {zahtjevi.map(z => (
            <tr key={z.id} className="border-b hover:bg-slate-50">
              <td className="px-4 py-3 text-xs">{formatDatePrint(z.datum_podnosenja) || formatDatePrint(z.created_at) || '-'}</td>
              <td className="px-4 py-3 text-sm font-medium">{z.objekat_naziv_adresa?.substring(0, 40) || '-'}</td>
              <td className="px-4 py-3 text-sm">{z.podnosilac_naziv || '-'}</td>
              <td className="px-4 py-3 text-sm text-center">{z.broj_uzoraka || 0}</td>
              <td className="px-4 py-3 text-sm text-slate-600">{z.created_by_name || '-'}</td>
              <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium border ${z.status === 'zavrsen' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : z.status === 'poslan' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{z.status === 'zavrsen' ? 'Završen' : z.status === 'poslan' ? 'Poslan' : 'Nacrt'}</span></td>
              <td className="px-4 py-3"><div className="flex gap-1">
                <button onClick={() => handlePrint(z)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600" /></button>
                <button onClick={() => handleWord(z)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600" /></button>
                <button onClick={() => handleEdit(z)} className="p-2 hover:bg-slate-200 rounded"><Pencil size={16} className="text-slate-600" /></button>
                <button onClick={() => handleDelete(z.id)} className="p-2 hover:bg-red-100 rounded"><Trash size={16} className="text-red-600" /></button>
              </div></td>
            </tr>
          ))}
        </tbody></table></div></Card>
      )}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Uredi zahtjev' : 'Novi zahtjev za briseve'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <Label className="text-xs font-semibold uppercase text-blue-700 mb-2 block">Auto-popunjavanje iz klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => ((v) => v !== 'none' && handleClientSelect(v))(id || 'none')} placeholder="Pretraga po nazivu ili PIB-u..." />
            </Card>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Naziv i adresa objekta *</Label><Input value={form.objekat_naziv_adresa} onChange={e => setForm({...form, objekat_naziv_adresa: e.target.value})} className="bg-white border-slate-300" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Broj uzoraka</Label><Input type="number" value={form.broj_uzoraka} onChange={e => setForm({...form, broj_uzoraka: parseInt(e.target.value)||0})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Vrsta analize</Label><Select value={form.vrsta_analize} onValueChange={v => setForm({...form, vrsta_analize: v})}><SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mikrobioloska">Mikrobiološka</SelectItem></SelectContent></Select></div>
            </div>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Podnosilac - Naziv *</Label><Input value={form.podnosilac_naziv} onChange={e => setForm({...form, podnosilac_naziv: e.target.value})} className="bg-white border-slate-300" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Adresa</Label><Input value={form.podnosilac_adresa} onChange={e => setForm({...form, podnosilac_adresa: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Kontakt tel</Label><Input value={form.podnosilac_kontakt} onChange={e => setForm({...form, podnosilac_kontakt: e.target.value})} className="bg-white border-slate-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">PIB</Label><Input value={form.podnosilac_pib} onChange={e => setForm({...form, podnosilac_pib: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">PDV</Label><Input value={form.podnosilac_pdv} onChange={e => setForm({...form, podnosilac_pdv: e.target.value})} className="bg-white border-slate-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Mjesto podnošenja</Label><Input value={form.mjesto_podnosenja} onChange={e => setForm({...form, mjesto_podnosenja: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Datum</Label><DateInput value={form.datum_podnosenja} onChange={e => setForm({...form, datum_podnosenja: e.target.value})} className="border-slate-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Kontakt osoba</Label><Input value={form.kontakt_osoba} onChange={e => setForm({...form, kontakt_osoba: e.target.value})} className="bg-white border-slate-300" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-500">Ovlašćeno lice</Label><Input value={form.ovlasceno_lice} onChange={e => setForm({...form, ovlasceno_lice: e.target.value})} className="bg-white border-slate-300" /></div>
            </div>
            <div><Label className="text-xs font-semibold uppercase text-slate-500">Napomena</Label><textarea value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[50px]" /></div>
            <div className="flex gap-3 justify-end pt-4 border-t"><Button variant="outline" onClick={() => setIsFormOpen(false)}>Odustani</Button><Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">{editingId ? 'Ažuriraj' : 'Kreiraj'}</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
